import type { StateCreator } from 'zustand';
import { tokenStorage } from '@/lib/tokenStorage';
import type { ScheduledMaintenancePayload } from '@/types/thoughtspot';

interface AppState {
  isSourceModalOpen: boolean;
  areSourceTableActionsDisabled: boolean;
  impersonatedUser?: {
    name: string;
    email: string;
  } | null;
  hasHydrated: boolean;
  /**
   * Dev-only override for the scheduled maintenance banner, set from Debug Tools.
   * When non-null it takes precedence over the Unleash variant so the banner can
   * be exercised locally without provisioning the flag. Never set in production
   * (Debug Tools only mounts under `import.meta.env.DEV`) and never persisted.
   */
  debugScheduledMaintenanceBanner: ScheduledMaintenancePayload | null;
}

interface AppActions {
  setSourceModalOpen: (isOpen: boolean) => void;
  // provides a global way to disable all source table actions while one is in progress
  setAreSourceTableActionsDisabled: (areSourceTableActionsDisabled: boolean) => void;
  setImpersonatedUser: (user: { name: string; email: string } | null) => void;
  exitImpersonation: () => void;
  setHasHydrated: (hasHydrated: boolean) => void;
  setDebugScheduledMaintenanceBanner: (payload: ScheduledMaintenancePayload | null) => void;
}

export interface AppSlice extends AppState, AppActions {}

const initialAppState: AppState = {
  isSourceModalOpen: false,
  areSourceTableActionsDisabled: false,
  impersonatedUser: null,
  hasHydrated: false,
  debugScheduledMaintenanceBanner: null,
};

export const createAppSlice: StateCreator<AppSlice, [['zustand/devtools', never]], [], AppSlice> = (set) => ({
  ...initialAppState,
  setSourceModalOpen: (isSourceModalOpen) => {
    set({ isSourceModalOpen }, false, 'app/setSourceModalOpen');
  },
  setAreSourceTableActionsDisabled: (areSourceTableActionsDisabled) => {
    set({ areSourceTableActionsDisabled }, false, 'app/areSourceTableActionsDisabled');
  },
  setImpersonatedUser: (impersonatedUser) => {
    set({ impersonatedUser }, false, 'app/setImpersonatedUser');
  },
  exitImpersonation: () => {
    // Clear SDK token from sessionStorage before reload
    tokenStorage.clearToken();

    // Clear impersonated user from store (will persist to localStorage)
    set({ impersonatedUser: null }, false, 'app/exitImpersonation');

    // Reload the page - this will refetch SDK token without X-Alt-User-Context header
    window.location.reload();
  },
  setHasHydrated: (hasHydrated) => {
    set({ hasHydrated }, false, 'app/setHasHydrated');
  },
  setDebugScheduledMaintenanceBanner: (debugScheduledMaintenanceBanner) => {
    set({ debugScheduledMaintenanceBanner }, false, 'app/setDebugScheduledMaintenanceBanner');
  },
});
